// src/components/vehicles/FaultReportForm.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import { AuthContext } from "../../context/AuthContext";
import { api } from "../../utils/api";
import "../../css/Fleet.css";

const FAULT_CATEGORIES = [
  { label: "Silnik", value: "Silnik", icon: "pi pi-bolt" },
  { label: "Układ hamulcowy", value: "Układ hamulcowy", icon: "pi pi-stop-circle" },
  { label: "Układ elektryczny", value: "Układ elektryczny", icon: "pi pi-power-off" },
  { label: "Układ hydrauliczny", value: "Układ hydrauliczny", icon: "pi pi-filter" },
  { label: "Opony / koła", value: "Opony / koła", icon: "pi pi-circle" },
  { label: "Skrzynia biegów", value: "Skrzynia biegów", icon: "pi pi-cog" },
  { label: "Nadwozie / kabina", value: "Nadwozie / kabina", icon: "pi pi-car" },
  { label: "Klimatyzacja / ogrzewanie", value: "Klimatyzacja / ogrzewanie", icon: "pi pi-sun" },
  { label: "Oświetlenie", value: "Oświetlenie", icon: "pi pi-eye" },
  { label: "Inne", value: "Inne", icon: "pi pi-question-circle" },
];

const PRIORITY_OPTIONS = [
  { label: "Niski", value: "low", color: "var(--fleet-success)", icon: "pi pi-arrow-down" },
  { label: "Normalny", value: "normal", color: "var(--fleet-primary)", icon: "pi pi-minus" },
  { label: "Wysoki", value: "high", color: "var(--fleet-warning)", icon: "pi pi-arrow-up" },
  { label: "Krytyczny", value: "critical", color: "var(--fleet-danger)", icon: "pi pi-exclamation-triangle" },
];

const FaultReportForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ vehicleId: null, category: null, description: "", priority: "normal", isExternal: false, extVehicleBrand: "", extVehicleModel: "", extVehicleRegistration: "" });
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [step, setStep] = useState(1);
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadVehicles(); }, []);

  const loadVehicles = async () => {
    try {
      const res = await api.get("/vehicles", { params: { limit: 1000, status: "ACTIVE" } });
      setVehicles((res.data?.data || []).map((v) => ({ ...v, label: `${v.brand} ${v.model} (${v.registration_number})` })));
    } catch (err) { console.error(err); }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    if (remaining <= 0) {
      toast.current.show({ severity: "warn", summary: "Limit", detail: "Maksymalnie 5 zdjęć" });
      return;
    }
    const toAdd = files.slice(0, remaining);
    setPhotos([...photos, ...toAdd]);
    const newPreviews = [...previews];
    toAdd.forEach((file) => newPreviews.push(URL.createObjectURL(file)));
    setPreviews(newPreviews);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(previews[index]);
    setPhotos(photos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setForm({ vehicleId: null, category: null, description: "", priority: "normal", isExternal: false, extVehicleBrand: "", extVehicleModel: "", extVehicleRegistration: "" });
    previews.forEach((url) => URL.revokeObjectURL(url));
    setPhotos([]);
    setPreviews([]);
    setSent(false);
    setStep(1);
  };

  const canGoNext = () => {
    if (step === 1) {
      if (form.isExternal) return form.extVehicleBrand && form.extVehicleRegistration && form.category;
      return form.vehicleId && form.category;
    }
    if (step === 2) return form.description && form.description.trim().length >= 10;
    return true;
  };

  const submitReport = async () => {
    if (!form.category || !form.description) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Wypełnij wszystkie wymagane pola" });
      return;
    }
    if (!form.isExternal && !form.vehicleId) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Wybierz pojazd" });
      return;
    }
    if (form.isExternal && (!form.extVehicleBrand || !form.extVehicleRegistration)) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Podaj markę i numer rejestracyjny pojazdu zewnętrznego" });
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("isExternal", form.isExternal);
      if (form.isExternal) {
        formData.append("extVehicleBrand", form.extVehicleBrand);
        formData.append("extVehicleModel", form.extVehicleModel);
        formData.append("extVehicleRegistration", form.extVehicleRegistration);
      } else {
        formData.append("vehicleId", form.vehicleId);
      }
      formData.append("category", form.category);
      formData.append("description", form.description);
      formData.append("priority", form.priority);
      photos.forEach((file) => formData.append("photos", file));
      await api.post("/fault-reports", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.current.show({ severity: "success", summary: "Wysłano!", detail: "Zgłoszenie usterki zostało zarejestrowane" });
      setSent(true);
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się wysłać zgłoszenia" });
    }
    setSending(false);
  };

  const selectedVehicle = vehicles.find(v => v.id === form.vehicleId);
  const selectedCategory = FAULT_CATEGORIES.find(c => c.value === form.category);
  const selectedPriority = PRIORITY_OPTIONS.find(p => p.value === form.priority);

  // --- Success screen ---
  if (sent) {
    return (
      <div className="fleet-page fleet-animate-in">
        <Toast ref={toast} />
        <div className="fault-success-screen">
          <div className="fault-success-icon">
            <i className="pi pi-check-circle" />
          </div>
          <h2 className="fault-success-title">Zgłoszenie wysłane!</h2>
          <p className="fault-success-text">
            Twoje zgłoszenie zostało zarejestrowane i przekazane do kierownika warsztatu.
          </p>
          <div className="fault-success-summary">
            <div className="fault-success-row">
              <span className="fault-success-label">Pojazd</span>
              <span>{form.isExternal ? `${form.extVehicleBrand} ${form.extVehicleModel} (${form.extVehicleRegistration}) — zewnętrzny` : (selectedVehicle?.label || "—")}</span>
            </div>
            <div className="fault-success-row">
              <span className="fault-success-label">Kategoria</span>
              <span>{form.category}</span>
            </div>
            <div className="fault-success-row">
              <span className="fault-success-label">Priorytet</span>
              <span style={{ color: selectedPriority?.color, fontWeight: 600 }}>{selectedPriority?.label}</span>
            </div>
            {photos.length > 0 && (
              <div className="fault-success-row">
                <span className="fault-success-label">Zdjęcia</span>
                <span>{photos.length} załączonych</span>
              </div>
            )}
          </div>
          <div className="fault-success-actions">
            <Button label="Zgłoś kolejną usterkę" icon="pi pi-plus" className="fleet-btn-primary" onClick={resetForm} />
            <Button label="Lista zgłoszeń" icon="pi pi-list" className="p-button-outlined" onClick={() => navigate("/warsztat")} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">⚠️</span>Zgłoś usterkę</h1>
          <p className="fleet-page-subtitle">Wypełnij formularz, aby zgłosić problem z pojazdem lub sprzętem</p>
        </div>
      </div>

      {/* Progress steps */}
      <div className="fault-steps">
        <div className={`fault-step ${step >= 1 ? "fault-step--active" : ""} ${step > 1 ? "fault-step--done" : ""}`}>
          <div className="fault-step-num">{step > 1 ? <i className="pi pi-check" /> : "1"}</div>
          <span className="fault-step-label">Pojazd i kategoria</span>
        </div>
        <div className="fault-step-line" />
        <div className={`fault-step ${step >= 2 ? "fault-step--active" : ""} ${step > 2 ? "fault-step--done" : ""}`}>
          <div className="fault-step-num">{step > 2 ? <i className="pi pi-check" /> : "2"}</div>
          <span className="fault-step-label">Opis problemu</span>
        </div>
        <div className="fault-step-line" />
        <div className={`fault-step ${step >= 3 ? "fault-step--active" : ""}`}>
          <div className="fault-step-num">3</div>
          <span className="fault-step-label">Zdjęcia i wysyłka</span>
        </div>
      </div>

      <div className="fault-form-layout">
        {/* Main form area */}
        <div className="fault-form-main">
          {/* Step 1: Vehicle & Category */}
          {step === 1 && (
            <div className="fleet-card fleet-animate-in">
              <div className="fleet-card-header">
                <h3 className="fleet-card-title"><i className="pi pi-car" /> Krok 1: Wybierz pojazd i kategorię</h3>
              </div>
              <div className="fleet-card-body">
                <div className="fault-form-fields">
                  <div className="fleet-form-field">
                    <label>Typ pojazdu</label>
                    <SelectButton
                      value={form.isExternal ? "external" : "company"}
                      options={[
                        { label: "Pojazd firmowy", value: "company" },
                        { label: "Pojazd zewnętrzny", value: "external" },
                      ]}
                      onChange={(e) => setForm({ ...form, isExternal: e.value === "external", vehicleId: null, extVehicleBrand: "", extVehicleModel: "", extVehicleRegistration: "" })}
                    />
                  </div>
                  {!form.isExternal ? (
                    <div className="fleet-form-field">
                      <label>Pojazd / sprzęt <span className="fault-required">*</span></label>
                      <Dropdown
                        value={form.vehicleId} options={vehicles}
                        onChange={(e) => setForm({ ...form, vehicleId: e.value })}
                        optionLabel="label" optionValue="id"
                        placeholder="Wybierz pojazd z listy..." filter
                        className="fault-dropdown"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="fleet-form-field">
                        <label>Marka <span className="fault-required">*</span></label>
                        <InputText value={form.extVehicleBrand} onChange={(e) => setForm({ ...form, extVehicleBrand: e.target.value })} placeholder="np. Volvo" />
                      </div>
                      <div className="fleet-form-field">
                        <label>Model</label>
                        <InputText value={form.extVehicleModel} onChange={(e) => setForm({ ...form, extVehicleModel: e.target.value })} placeholder="np. FH16" />
                      </div>
                      <div className="fleet-form-field">
                        <label>Numer rejestracyjny <span className="fault-required">*</span></label>
                        <InputText value={form.extVehicleRegistration} onChange={(e) => setForm({ ...form, extVehicleRegistration: e.target.value })} placeholder="np. WA 12345" />
                      </div>
                    </>
                  )}
                  <div className="fleet-form-field">
                    <label>Kategoria awarii <span className="fault-required">*</span></label>
                    <Dropdown
                      value={form.category} options={FAULT_CATEGORIES}
                      onChange={(e) => setForm({ ...form, category: e.value })}
                      placeholder="Wybierz kategorię..."
                      className="fault-dropdown"
                      itemTemplate={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <i className={opt.icon} style={{ fontSize: "0.9rem", color: "var(--fleet-text-secondary)" }} />
                          <span>{opt.label}</span>
                        </div>
                      )}
                    />
                  </div>
                  <div className="fleet-form-field">
                    <label>Priorytet</label>
                    <div className="fault-priority-grid">
                      {PRIORITY_OPTIONS.map(p => (
                        <button key={p.value} type="button"
                          className={`fault-priority-btn ${form.priority === p.value ? "fault-priority-btn--active" : ""}`}
                          style={{ "--priority-color": p.color }}
                          onClick={() => setForm({ ...form, priority: p.value })}>
                          <i className={p.icon} />
                          <span>{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Description */}
          {step === 2 && (
            <div className="fleet-card fleet-animate-in">
              <div className="fleet-card-header">
                <h3 className="fleet-card-title"><i className="pi pi-pencil" /> Krok 2: Opisz problem</h3>
              </div>
              <div className="fleet-card-body">
                <div className="fault-form-fields">
                  <div className="fleet-form-field">
                    <label>Opis problemu <span className="fault-required">*</span></label>
                    <InputTextarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={6} autoResize
                      placeholder="Opisz szczegółowo: co się dzieje, jakie objawy, kiedy problem się pojawił, czy powtarza się regularnie..."
                      className="fault-textarea"
                    />
                    <div className="fault-char-count">
                      {form.description.length} znaków
                      {form.description.length > 0 && form.description.trim().length < 10 && (
                        <span className="fault-char-warn"> (minimum 10 znaków)</span>
                      )}
                    </div>
                  </div>
                  <div className="fault-tips">
                    <div className="fault-tips-title"><i className="pi pi-info-circle" /> Wskazówki</div>
                    <ul>
                      <li>Opisz dokładnie objawy usterki</li>
                      <li>Podaj kiedy problem się pojawił</li>
{/*                       <li>Napisz czy usterka uniemożliwia jazdę</li>
                      <li>Wspomnij o ewentualnych dźwiękach, zapachach, wibracjach</li> */}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Photos & Submit */}
          {step === 3 && (
            <div className="fleet-card fleet-animate-in">
              <div className="fleet-card-header">
                <h3 className="fleet-card-title"><i className="pi pi-camera" /> Krok 3: Dodaj zdjęcia i wyślij</h3>
              </div>
              <div className="fleet-card-body">
                <div className="fault-form-fields">
                  <div className="fleet-form-field">
                    <label>Dokumentacja fotograficzna <span style={{ color: "var(--fleet-text-muted)", fontWeight: 400 }}>(opcjonalne, maks. 5)</span></label>
                    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display: "none" }} />
                    <div className="fault-photos-grid">
                      {previews.map((url, i) => (
                        <div key={i} className="fault-photo-item">
                          <img src={url} alt={`Zdjęcie ${i + 1}`} />
                          <button type="button" className="fault-photo-remove" onClick={() => removePhoto(i)}>
                            <i className="pi pi-times" />
                          </button>
                        </div>
                      ))}
                      {photos.length < 5 && (
                        <button type="button" className="fault-photo-add" onClick={() => fileInputRef.current.click()}>
                          <i className="pi pi-camera" />
                          <span>Dodaj zdjęcie</span>
                          <span className="fault-photo-add-sub">{photos.length}/5</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Summary before sending */}
                  <div className="fault-summary-card">
                    <div className="fault-summary-title"><i className="pi pi-list" /> Podsumowanie zgłoszenia</div>
                    <div className="fault-summary-rows">
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Pojazd</span>
                        <span className="fault-summary-value">{selectedVehicle?.label || "—"}</span>
                      </div>
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Kategoria</span>
                        <span className="fault-summary-value">{form.category || "—"}</span>
                      </div>
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Priorytet</span>
                        <span className="fault-summary-value" style={{ color: selectedPriority?.color, fontWeight: 600 }}>
                          <i className={selectedPriority?.icon} style={{ marginRight: "0.3rem" }} />{selectedPriority?.label}
                        </span>
                      </div>
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Opis</span>
                        <span className="fault-summary-value fault-summary-desc">{form.description || "—"}</span>
                      </div>
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Zdjęcia</span>
                        <span className="fault-summary-value">{photos.length > 0 ? `${photos.length} załączonych` : "Brak"}</span>
                      </div>
                      <div className="fault-summary-row">
                        <span className="fault-summary-label">Zgłaszający</span>
                        <span className="fault-summary-value">{user?.forname} {user?.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="fault-nav-buttons">
            {step > 1 && (
              <Button label="Wstecz" icon="pi pi-arrow-left" className="p-button-outlined" onClick={() => setStep(step - 1)} />
            )}
            <div style={{ flex: 1 }} />
            {step < 3 ? (
              <Button label="Dalej" icon="pi pi-arrow-right" iconPos="right" className="fleet-btn-primary"
                onClick={() => setStep(step + 1)} disabled={!canGoNext()} />
            ) : (
              <Button label={sending ? "Wysyłanie..." : "Wyślij zgłoszenie"} icon={sending ? "pi pi-spin pi-spinner" : "pi pi-send"}
                className="fleet-btn-primary" onClick={submitReport} disabled={sending} />
            )}
          </div>
        </div>

        {/* Sidebar - visible on desktop */}
        <div className="fault-form-sidebar">
          <div className="fleet-card">
            <div className="fleet-card-body">
              <div className="fault-sidebar-info">
                <i className="pi pi-info-circle" style={{ fontSize: "1.2rem", color: "var(--fleet-primary)" }} />
                <h4>Jak zgłosić usterkę?</h4>
                <ol>
                  <li>Wybierz pojazd i kategorię awarii</li>
                  <li>Opisz problem jak najdokładniej</li>
                  <li>Dodaj zdjęcia dokumentujące usterkę</li>
                  <li>Wyślij zgłoszenie</li>
                </ol>
                <p className="fault-sidebar-note">
                  Zgłoszenie trafi do kierownika warsztatu, który przydzieli naprawę.
                </p>
              </div>
            </div>
          </div>
          {(form.vehicleId || form.category) && (
            <div className="fleet-card" style={{ marginTop: "0.75rem" }}>
              <div className="fleet-card-body">
                <div className="fault-sidebar-preview">
                  <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.85rem", color: "var(--fleet-text-secondary)" }}>Twoje zgłoszenie</h4>
                  {selectedVehicle && (
                    <div className="fault-preview-item">
                      <i className="pi pi-car" /> {selectedVehicle.label}
                    </div>
                  )}
                  {selectedCategory && (
                    <div className="fault-preview-item">
                      <i className={selectedCategory.icon} /> {selectedCategory.label}
                    </div>
                  )}
                  {selectedPriority && (
                    <div className="fault-preview-item" style={{ color: selectedPriority.color }}>
                      <i className={selectedPriority.icon} /> {selectedPriority.label}
                    </div>
                  )}
                  {form.description && (
                    <div className="fault-preview-desc">
                      {form.description.length > 80 ? form.description.substring(0, 80) + "..." : form.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaultReportForm;
